import { createRootRoute, Outlet, useLocation, HeadContent } from "@tanstack/react-router";
// import { TanStackRouterDevtools } from "@tanstack/router-devtools";
import { AnimatePresence, motion } from "framer-motion";
import { BottomNav } from "@shared/components/BottomNav";
import { CookieConsent } from "@shared/components/CookieConsent";
import { AppHeader } from "@shared/components/AppHeader";
import { AppHeaderProvider, useAppHeader } from "@shared/components/AppHeaderContext";
import { useEffect, useState } from "react";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { getCurrentLanguage } from "@shared/lib/i18n";
import { USER_SESSION_KEY } from "@shared/hooks/useUserSession";

// Animații pentru tranziții între pagini
const pageVariants = {
  initial: { 
    opacity: 0,
    y: 8,
  },
  animate: { 
    opacity: 1,
    y: 0,
  },
  exit: { 
    opacity: 0,
    y: -8,
  },
};

const pageTransition = {
  type: "tween" as const,
  duration: 0.2,
  ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number],
};

// Inner component care folosește context
function RootLayoutInner() {
  const location = useLocation();
  const isAppRoute = location.pathname.startsWith("/app");
  const isOfferDetailPage = location.pathname.match(/^\/app\/offers\/[^/]+$/);
  const showBottomNav = isAppRoute && !isOfferDetailPage;
  const showAppHeader = isAppRoute && !isOfferDetailPage;

  const currentLang = getCurrentLanguage();
  const [userId, setUserId] = useState<Id<"users"> | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const headerContext = useAppHeader();

  // Obține date pentru invites stats
  const invites = useQuery(
    api.invites.getUserInvites,
    userId && sessionToken ? { userId, sessionToken } : "skip"
  );
  const inviteTree = useQuery(
    api.invites.getInviteTree,
    userId && sessionToken ? { userId, sessionToken } : "skip"
  );

  const invitesStats = invites
    ? {
        available: invites.filter((i) => i.active && !i.usedBy && Date.now() < i.expiresAt).length,
        used: invites.filter((i) => i.usedBy).length,
        activated: inviteTree?.length || 0,
      }
    : undefined;

  // Citește userId din localStorage
  useEffect(() => {
    if (isAppRoute) {
      const storedUserId = localStorage.getItem("userId");
      setSessionToken(localStorage.getItem(USER_SESSION_KEY));
      if (storedUserId) {
        setUserId(storedUserId as Id<"users">);
      }
    }
  }, [isAppRoute]);

  // Obține datele utilizatorului din Convex
  const user = useQuery(
    api.auth.getCurrentUser,
    userId ? { userId } : "skip"
  );

  // Obține categorii pentru offers header
  const categories = useQuery(api.offers.getCategories, isAppRoute ? {} : "skip");

  // Map themes
  const mapThemes = [
    { id: "default", name: currentLang === "ro" ? "Standard" : "Standard", icon: "🗺️" },
    { id: "positron", name: currentLang === "ro" ? "Minimal" : "Minimal", icon: "🌍" },
  ];

  // Lazy init PostHog pe rute publice și în app
  useEffect(() => {
    const isTrackedRoute =
      location.pathname === '/' ||
      location.pathname === '/join' ||
      location.pathname === '/pentru-business' ||
      location.pathname === '/public-landing' ||
      location.pathname.startsWith('/app');

    // initPostHogLazy verifică singur consimțământul, deci aici decidem doar
    // pe ce rute are sens măsurarea.
    if (isTrackedRoute) {
      import("@shared/lib/posthog-lazy").then(({ initPostHogLazy, trackPageView }) => {
        initPostHogLazy().then(() => {
          trackPageView(location.pathname);
        });
      });
    }
  }, [location.pathname]);

  // Init PostHog și identificare user după login
  useEffect(() => {
    if (user && userId && isAppRoute) {
      import("@shared/lib/posthog-lazy").then(({ initPostHogLazy, identifyUser }) => {
        initPostHogLazy().then(() => {
          identifyUser(userId, {
            email: user.email,
            membershipStatus: user.membershipStatus,
          });
        });
      });
    }
  }, [user, userId, isAppRoute]);

  return (
    <>
      <HeadContent />
      {/* `clip`, nu `hidden`: overflow-x: hidden forțează overflow-y: auto și
          transformă wrapperul într-un scroll container care nu scrollează
          niciodată. Orice `view()` timeline dinăuntru s-ar măsura față de el și
          ar rămâne blocat la 50%. `clip` taie la fel pe orizontală, dar lasă
          axa verticală `visible`, deci pagina rămâne scrollerul real. */}
      <div className="min-h-screen bg-background overflow-x-clip">
      {/* AppHeader persistent - doar pe rutele /app/*, dar nu pe pagina de ofertă individuală */}
      {showAppHeader && user && (
        <AppHeader
          user={user}
          currentLang={currentLang}
          offersSearchQuery={headerContext.offersSearchQuery}
          onOffersSearchChange={headerContext.setOffersSearchQuery}
          offersViewMode={headerContext.offersViewMode}
          onOffersViewModeChange={headerContext.setOffersViewMode}
          offersSelectedCategory={headerContext.offersSelectedCategory}
          onOffersCategoryChange={headerContext.setOffersSelectedCategory}
          offersSortBy={headerContext.offersSortBy}
          onOffersSortChange={headerContext.setOffersSortBy}
          offersShowFilters={headerContext.offersShowFilters}
          onOffersFiltersToggle={() => headerContext.setOffersShowFilters(!headerContext.offersShowFilters)}
          offersCategories={categories}
          mapTheme={headerContext.mapTheme}
          onMapThemeChange={headerContext.setMapTheme}
          mapThemes={mapThemes}
          invitesStats={invitesStats}
        />
      )}
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={location.pathname}
          variants={pageVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={pageTransition}
          className="min-h-screen w-full"
        >
          <Outlet />
        </motion.div>
      </AnimatePresence>
      {/* BottomNav persistent - doar pe rutele /app/*, dar nu pe pagina de ofertă individuală */}
      {showBottomNav && <BottomNav />}
      <CookieConsent />
      {/* {import.meta.env.DEV && <TanStackRouterDevtools position="bottom-right" />} */}
    </div>
    </>
  );
}

// Layout principal cu provider
function RootLayout() {
  return (
    <AppHeaderProvider>
      <RootLayoutInner />
    </AppHeaderProvider>
  );
}

// Ruta principală
export const Route = createRootRoute({
  head: () => ({
    meta: [
      { name: "theme-color", content: "#6D5EF7" },
      { name: "msapplication-TileColor", content: "#6D5EF7" },
    ],
  }),
  component: RootLayout,
});
